import React, { useState, useEffect } from 'react';
import { UserProfile, UserRole, CreditRequest } from '../../types';
import { api } from '../../services/api';

export const BillingTab: React.FC<{ user: UserProfile }> = ({ user }) => {
    // --- 1. Wallet & Credit State ---
    const [amount, setAmount] = useState(500); 
    const [requests, setRequests] = useState<CreditRequest[]>([]);
    const [loading, setLoading] = useState(false);

    // --- 2. Billing Details State (Initialized with User Profile Data) ---
    const [billingDetails, setBillingDetails] = useState({
        admin_organization: user.admin_organization || '',
        admin_website: user.admin_website || '',
        admin_gst: user.admin_gst || '',
        admin_address: user.admin_address || '',
        admin_city: user.admin_city || '',
        admin_state: user.admin_state || '',
        admin_pincode: user.admin_pincode || '',
        admin_country: user.admin_country || 'India',
    });

    // Load credit requests on mount
    useEffect(() => { 
        api.getCreditRequests(UserRole.ADMIN, user.id).then(setRequests); 
    }, [user.id]);

    // Handle sending a recharge request
    const handleRequest = async () => {
        try { 
            await api.requestCredits(user.id, amount); 
            setRequests(await api.getCreditRequests(UserRole.ADMIN, user.id)); 
            alert('Request Sent Successfully'); 
        } catch { 
            alert('Error sending request'); 
        }
    };

    // Handle saving profile details (Organization info)
    const handleSaveDetails = async () => {
        setLoading(true);
        try {
            console.log("Saving admin details:", billingDetails);
            
            // ✅ Use the API service to update details
            await api.updateAdminBillingDetails(user.id, billingDetails);

            alert('Billing details updated successfully');
        } catch (e: any) {
            console.error("Save failed:", e);
            alert(`Failed to save details: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    // --- 3. Overdraft Logic ---
    const currentCredits = user.credits || 0;
    const isLowBalance = currentCredits < 0; // Trigger warning if negative
    const isBlocked = currentCredits <= -100; // Visual indicator for blocked state

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* LEFT COLUMN: Wallet & Requests */}
            <div className="space-y-8">
                {/* Credit Balance Card */}
                <div className={`rounded-2xl p-8 shadow-lg transition-colors border ${
                    isLowBalance 
                        ? 'bg-red-50 border-red-500 text-red-900' // NEGATIVE / OVERDRAFT STYLE
                        : 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-transparent' // NORMAL STYLE
                }`}>
                    <h2 className="text-4xl font-bold tracking-tight">
                        {currentCredits} Credits
                    </h2>
                    <p className={`mt-2 font-medium ${isLowBalance ? 'text-red-700' : 'opacity-90'}`}>
                        {isBlocked 
                            ? '🚫 ACCOUNT BLOCKED (Limit Reached)' 
                            : isLowBalance 
                                ? '⚠️ Overdraft Active (Limit: -100)' 
                                : 'Available Balance'
                        }
                    </p>
                </div>
                
                {/* Recharge Section */}
                <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                    <h3 className="font-bold text-lg mb-4 text-slate-800">Recharge Wallet</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">Amount (Credits)</label>
                            <input 
                                type="number" 
                                value={amount} 
                                onChange={e => setAmount(Number(e.target.value))} 
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all" 
                            />
                        </div>
                        <button 
                            onClick={handleRequest} 
                            className="w-full bg-slate-900 text-white py-2.5 rounded-lg hover:bg-slate-800 transition-colors font-medium shadow-md active:scale-95 duration-200"
                        >
                            Request Recharge
                        </button>
                    </div>
                </div>

                {/* Recent Requests List */}
                {requests.length > 0 && (
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                        <h3 className="font-bold text-lg mb-4 text-slate-800">Recent Requests</h3>
                        <div className="space-y-3">
                            {requests.slice(0, 3).map((req, i) => (
                                <div key={i} className="flex justify-between items-center text-sm p-3 bg-slate-50 rounded-lg">
                                    <span className="font-medium text-slate-700">{req.amount} Credits</span>
                                    <span className={`px-2 py-1 rounded-full text-xs font-bold capitalize ${
                                        req.status === 'approved' ? 'bg-green-100 text-green-700' :
                                        req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                        'bg-yellow-100 text-yellow-700'
                                    }`}>
                                        {req.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* RIGHT COLUMN: Admin Organization Details */}
            <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm h-fit">
                <h3 className="font-bold text-xl mb-6 text-slate-800 border-b pb-4">
                    Organization Details
                </h3>
                
                <div className="space-y-5">
                    {/* Organization Name */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Organization Name</label>
                        <input 
                            type="text" 
                            value={billingDetails.admin_organization}
                            onChange={e => setBillingDetails({...billingDetails, admin_organization: e.target.value})}
                            className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                            placeholder="My Company Pvt Ltd"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        {/* Website */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Website</label>
                            <input 
                                type="text" 
                                value={billingDetails.admin_website}
                                onChange={e => setBillingDetails({...billingDetails, admin_website: e.target.value})}
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                                placeholder="www.example.com"
                            />
                        </div>

                        {/* GST Number */}
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">GST Number</label>
                            <input 
                                type="text" 
                                value={billingDetails.admin_gst}
                                onChange={e => setBillingDetails({...billingDetails, admin_gst: e.target.value})}
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none uppercase"
                                placeholder="22AAAAA0000A1Z5"
                            />
                        </div>
                    </div>

                    {/* Address */}
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Registered Address</label>
                        <textarea 
                            value={billingDetails.admin_address}
                            onChange={e => setBillingDetails({...billingDetails, admin_address: e.target.value})}
                            rows={2}
                            className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none resize-none"
                            placeholder="Building No, Street Name"
                        />
                    </div>

                    {/* City & State */}
                    <div className="grid grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">City</label>
                            <input 
                                type="text" 
                                value={billingDetails.admin_city}
                                onChange={e => setBillingDetails({...billingDetails, admin_city: e.target.value})}
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                            />
                        </div>
                        
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">State</label>
                            <input 
                                type="text" 
                                value={billingDetails.admin_state}
                                onChange={e => setBillingDetails({...billingDetails, admin_state: e.target.value})}
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                            />
                        </div>
                    </div>

                    {/* Pincode & Country */}
                    <div className="grid grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pincode</label>
                            <input 
                                type="text" 
                                value={billingDetails.admin_pincode}
                                onChange={e => setBillingDetails({...billingDetails, admin_pincode: e.target.value})}
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Country</label>
                            <input 
                                type="text" 
                                value={billingDetails.admin_country}
                                onChange={e => setBillingDetails({...billingDetails, admin_country: e.target.value})}
                                className="w-full border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                            />
                        </div>
                    </div>

                    <button 
                        onClick={handleSaveDetails}
                        disabled={loading}
                        className={`w-full mt-6 py-3 rounded-lg text-white font-semibold shadow-md transition-all 
                            ${loading ? 'bg-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg active:scale-[0.98]'}
                        `}
                    >
                        {loading ? 'Saving...' : 'Save Organization Details'}
                    </button>
                </div>
            </div>
        </div>
    );
};
